import { TSupportedLanguageKey } from "i18n";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import { consultantProfileType, IConsultantProfile } from "@/data/consultant-profiles";
import { TLegalDocumentType } from "@/data/legal-documents";
import { configUserType, IUser } from "@/data/users";

export interface ICredentials {
  username: string;
  password: string;
}

export interface IRegisterUser {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  language: TSupportedLanguageKey;
  agreements: {
    accessAgreement: boolean;
  };
  onboardingJobRequestId?: number;
}

export interface IPasswordReset {
  email: string;
}

export interface IPasswordSetup {
  password: string;
  confirmPassword: string;
  token: string;
}

export type TPasswordResetStatus = "VALID" | "USED" | "EXPIRED";

export interface IPasswordResetStatus {
  status: TPasswordResetStatus;
}

export interface IEmailConfirmation {
  token: string;
}

export interface IEmailConfirmationResponse {
  email: string;
}

export interface IPasswordSetupResponse {
  email: string;
}

export interface IResendEmailConfirmation {
  email: string;
}

export interface ISocialRegistration extends Pick<IRegisterUser, "language"> {
  agreements: {
    accessAgreement: boolean;
  };
  tokenUUID: string;
}

export interface ISocialLogin {
  token: string;
}

export type TOAuthServiceType = "LinkedIn" | "Google";
export type TOAuthActionType = "login" | "register-form";

export type TUserRole = "CONSULTANT" | "MANAGER" | "ADMIN";

export interface IUserRole {
  role: TUserRole;
  companyId: number;
}

export const configUserRole: TType<IUserRole> = {
  role: stringType(""),
  companyId: numberType(null),
};

export const userRole = ensure(configUserRole);

export interface IUserEmail {
  confirmed: boolean;
  primary: boolean;
  email: string;
}

export const configUserEmailType: TType<IUserEmail> = {
  confirmed: booleanType(false),
  primary: booleanType(false),
  email: stringType(""),
};

export const userEmail = ensure(configUserEmailType);

export interface ISessionUser extends IUser {
  consultantProfiles: IConsultantProfile[];
  emails: IUserEmail[];
  roles: IUserRole[];
  updatedLegalDocuments: TLegalDocumentType[];
}

const configSessionUserType: TType<ISessionUser> = {
  ...configUserType,
  consultantProfiles: ensureArray(consultantProfileType),
  roles: ensureArray(userRole),
  emails: ensureArray(userEmail),
  updatedLegalDocuments: ensureArray(stringType("")),
};

export const sessionUserType = ensure(configSessionUserType);
